import type { GameStateData } from '../game/GameState.js';
import type { Difficulty, DifficultyStrategy } from './DifficultyStrategy.js';
import type { AiAction, AiChoice } from './types.js';
import type { HardWorkerSearcher, SearchOptions } from './worker/HardWorkerSearcher.js';

export interface AiPlayerOptions {
  /** Engine seat index this AI owns (constant for the duration of a hand). */
  readonly seat: number;
  readonly strategy: DifficultyStrategy;
  /**
   * Minimum delay between "your turn" and the action being emitted. Search
   * is fast enough to be perceived as instant; the delay just keeps the AI
   * from feeling jittery and gives the player time to read the previous
   * play before the AI's response lands.
   */
  readonly minThinkMs: number;
  readonly onAction: (action: AiAction, choice: AiChoice) => void;
  /**
   * Optional Web Worker bridge. When provided AND `difficulty === 'hard'`,
   * search runs off the main thread instead of through `strategy.pickAction`.
   * If absent (e.g. in node tests), Hard tier still works via the in-process
   * `IsmctsSearcher` baked into `DifficultyStrategy`.
   */
  readonly asyncHardSearcher?: HardWorkerSearcher | null;
  readonly hardSearchOptions?: SearchOptions;
}

/**
 * Orchestrates one AI seat. The host (`main.ts`) calls `notifyTurn(state)`
 * whenever the AI's seat becomes the current seat (or whenever a Muggins
 * window opens with this AI as the scorer). The player computes an action
 * via `DifficultyStrategy`, waits out the minimum think delay, then emits it
 * via `onAction`. The host applies the action to the engine and re-notifies
 * on the next AI turn.
 *
 * Idempotent: a second `notifyTurn` while a previous one is still pending
 * is a no-op. `cancel()` drops the pending timer — call on navigation or
 * settings/difficulty change.
 */
const DEFAULT_HARD_SEARCH_OPTIONS: SearchOptions = {
  iterations: 4000,
  deadlineMs: 1200,
};

export class AiPlayer {
  private difficulty: Difficulty = 'medium';
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingCancel: (() => void) | null = null;
  private generation = 0;

  constructor(private readonly opts: AiPlayerOptions) {}

  get seat(): number {
    return this.opts.seat;
  }

  setDifficulty(difficulty: Difficulty): void {
    this.difficulty = difficulty;
  }

  cancel(): void {
    this.generation += 1;
    if (this.pendingTimer !== null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    if (this.pendingCancel !== null) {
      this.pendingCancel();
      this.pendingCancel = null;
    }
  }

  /**
   * The AI's seat is currently in control of the surface (active player OR
   * pending Muggins scorer). Pick an action and emit it asynchronously.
   *
   * Idempotent: returns immediately if a pending action is already queued.
   */
  notifyTurn(state: GameStateData): void {
    if (state.handOver || state.matchOver) return;
    const pending = state.pendingMuggins;
    const isOwnTurn = state.currentSeat === this.opts.seat;
    // Penalty scoring (Козёл) has no Muggins; never treat a stray window as ours.
    const isOwnMuggins =
      state.ruleSet.scoring === 'fives' && pending !== null && pending.scorerSeat === this.opts.seat;
    if (!isOwnTurn && !isOwnMuggins) return;
    if (this.pendingTimer !== null || this.pendingCancel !== null) return;

    const startMs = performance.now();
    const generation = this.generation;
    const useWorker =
      this.difficulty === 'hard' &&
      this.opts.asyncHardSearcher !== null &&
      this.opts.asyncHardSearcher !== undefined &&
      // Don't shell out for Muggins claims — the strategy emits them locally.
      !isOwnMuggins;

    if (useWorker && this.opts.asyncHardSearcher) {
      const opts = this.opts.hardSearchOptions ?? DEFAULT_HARD_SEARCH_OPTIONS;
      const { promise, cancel } = this.opts.asyncHardSearcher.search(state, this.opts.seat, opts);
      this.pendingCancel = cancel;
      promise
        .then((result) => {
          this.pendingCancel = null;
          if (generation !== this.generation) return;
          const choice: AiChoice = { action: result.action, score: result.iterations, depth: 2 };
          this.scheduleEmit(choice, generation, startMs);
        })
        .catch(() => {
          // On worker failure, fall back to the in-process strategy so the
          // hand doesn't stall. Re-notify to drive that path.
          this.pendingCancel = null;
          if (generation !== this.generation) return;
          const choice = this.opts.strategy.pickAction(state, this.opts.seat, this.difficulty);
          this.scheduleEmit(choice, generation, startMs);
        });
      return;
    }

    const choice = this.opts.strategy.pickAction(state, this.opts.seat, this.difficulty);
    this.scheduleEmit(choice, generation, startMs);
  }

  private scheduleEmit(choice: AiChoice, generation: number, startMs: number): void {
    const elapsed = performance.now() - startMs;
    const wait = Math.max(0, this.opts.minThinkMs - elapsed);
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (generation !== this.generation) return;
      this.opts.onAction(choice.action, choice);
    }, wait);
  }
}
